import { useContext, useEffect } from 'react';

import { CloseOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { setSessionStorageItem } from '@revenuemarket/browser';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays, OverlayContext } from '@revenuemarket/overlays';
import { Toast, Splitted } from '@revenuemarket/ui';
import { correctFloat } from '@revenuemarket/utils';
import { Button, Divider, Dropdown, Form, Tooltip, Typography } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { CompanyUserPermission } from '~apis/role/type';
import SettlementTaxInvoiceResultDrawer from '~components/BusinessEntity/SettlementTaxInvoiceResultDrawer';
import CategorySimpleLabelSelctor from '~components/Labeling/LabelManagement/CategorySimpleLabelSelctor';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import { formatNumByTaxInvoice } from '~components/Settlement/utils';
import IssueButton from '~components/TaxInvoice/IssueButton';
import TransactionDetailDrawer from '~components/Transactions/modals/TransactionDetailModal';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import {
  useGetTransactionLabelMappings,
  useMappingTaxInvoiceAndSplitToTransaction,
} from '~hooks/queries/mapping/query';
import { afterLabelingActions, useTransaction } from '~hooks/queries/transactions/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { AccountType } from '~models/bank/enum';
import { CashFlowType } from '~models/cashFlow';
import { mixpanel } from '~pages/_app';

import GroupLabelSelect from './GroupLabelSelect';
import { MappingTotal, TaxInvoiceMappingItem, TransactionMappingItem } from './MappingItem';
import SplittedItem from './SplittedItem';
import useTransactionMappingStore from './TransactionMappingStore';

interface Props {
  transactionId: number;
  mode: 'FOLD' | 'FULL';
  onChangeMode?: () => void;
  onMappingComplete?: () => void;
  onClose?: () => void;
}

const TransactionMappingTargetList: React.FC<Props> = ({
  transactionId,
  mode,
  onChangeMode,
  onClose,
  onMappingComplete,
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const { closeAll } = useContext(OverlayContext);
  const { data, refetch } = useGetTransactionLabelMappings({ companyId, transactionId });

  const { mutateAsync: mappingAndSplit, isLoading } = useMappingTaxInvoiceAndSplitToTransaction();

  const transactionLabelMappings = data?.Data?.data;

  const { data: transactionDetail } = useTransaction({
    companyId,
    transactionId,
    accountType: transactionLabelMappings?.accountType || AccountType.Checking,
  });

  const transactionDetailData = transactionDetail?.Data?.data;

  const { data: treeData } = useCategoriesTree({ companyId });
  const categoryTrees = treeData?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categoryTrees ?? []).filter((item) => !!item.value?.name);

  const {
    splittedTransactions,
    initialize,
    mappedInvoices,
    deleteMappedInvoice,
    deleteSplittedTransaction,
    addSplittedTransaction,
    setMappedInvoice,
  } = useTransactionMappingStore();

  useEffect(() => {
    if (transactionLabelMappings) {
      const invoices = transactionLabelMappings.labelMappings
        .filter((m) => m.mappedInvoice)
        .map((item) => {
          return {
            taxInvoiceType: item.mappedInvoice.taxInvoiceType,
            counterPartyCompanyName: item.mappedInvoice.counterPartyCompanyName,
            counterPartyBusinessRegNumber: item.mappedInvoice.counterPartyBusinessRegNumber,
            repItems: item.mappedInvoice.repItems,
            totalAmount: item.mappedInvoice.totalAmount,
            mappedAmount: formatNumByTaxInvoice(
              item.transactionLabelAmount,
              item.mappedInvoice.taxInvoiceType,
            ),
            categoryLabelKey: item.categoryLabelKey,
            groupLabelId: item.groupLabelId,
            reportingDate: item.mappedInvoice.reportingDate,
            taxInvoiceId: item.mappedInvoice.taxInvoiceId,
            transactionLabelAmount: item.transactionLabelAmount,
            counterPartyName: item.mappedInvoice.counterPartyName,
            businessEntityId: item.businessEntityLabelId,
          };
        });

      const splittedTransactions = transactionLabelMappings.labelMappings
        .filter((m) => !m.mappedInvoice)
        .map((item) => {
          return {
            groupLabelId: item.groupLabelId,
            amount: Math.abs(item.transactionLabelAmount),
            categoryLabelKey: item.categoryLabelKey,
            businessEntityId: item.businessEntityLabelId,
            cashFlowType: item.transactionLabelAmount > 0 ? CashFlowType.IN : CashFlowType.OUT,
          };
        });

      initialize({
        originalMappedInvoices: invoices,
        mappedInvoices: invoices,
        splittedTransactions:
          transactionLabelMappings.labelMappings.length > 1 ? splittedTransactions : [],
      });
    }
  }, [transactionLabelMappings]);

  if (!transactionLabelMappings) return null;

  const mappingAmount =
    mappedInvoices.reduce((acc, cur) => {
      if (transactionLabelMappings.transactionAmount > 0) {
        return acc + (cur.taxInvoiceType === 'SALES' ? cur.mappedAmount : -cur.mappedAmount);
      } else {
        return acc + (cur.taxInvoiceType === 'PURCHASE' ? cur.mappedAmount : -cur.mappedAmount);
      }
    }, 0) +
    splittedTransactions.reduce((acc, cur) => {
      if (transactionLabelMappings.transactionAmount > 0) {
        return acc + (cur?.amount ?? 0) * (cur.cashFlowType === 'IN' ? 1 : -1);
      } else {
        return acc + (cur?.amount ?? 0) * (cur.cashFlowType === 'OUT' ? 1 : -1);
      }
    }, 0);
  const targetAmount = Math.abs(transactionLabelMappings.transactionAmount);

  const targetUnMappedAmount = targetAmount - mappingAmount;

  const unMappingAmount = correctFloat(targetUnMappedAmount);

  const allMappingLabels = [
    ...splittedTransactions.map((s) => {
      const categoryId = s.categoryLabelKey?.split('_')[0]
        ? Number(s.categoryLabelKey?.split('_')[0])
        : null;
      const companyDetailedCategoryId = s.categoryLabelKey?.split('_')[1]
        ? Number(s.categoryLabelKey?.split('_')[1])
        : null;

      return {
        amount: (s.amount ?? 0) * (s.cashFlowType === CashFlowType.IN ? 1 : -1),
        categoryId: categoryId ?? 0,
        companyDetailedCategoryId,
        customLabelId: s.groupLabelId,
        businessEntityId: s.businessEntityId,
        invoiceId: null,
        invoiceType: null,
      };
    }),
    ...mappedInvoices.map((m) => {
      const categoryId = m.categoryLabelKey?.split('_')[0]
        ? Number(m.categoryLabelKey?.split('_')[0])
        : null;
      const companyDetailedCategoryId = m.categoryLabelKey?.split('_')[1]
        ? Number(m.categoryLabelKey?.split('_')[1])
        : null;
      const getAmount = () => {
        return m.taxInvoiceType === 'SALES' ? m.mappedAmount : -m.mappedAmount;
      };

      return {
        amount: getAmount(),
        categoryId: categoryId ?? 0,
        companyDetailedCategoryId,
        customLabelId: m.groupLabelId,
        businessEntityId: null,
        invoiceId: m.taxInvoiceId,
        invoiceType: m.taxInvoiceType,
      };
    }),
  ];

  const onMapping = async () => {
    const response = await mappingAndSplit({
      companyId,
      transactionId,
      data: {
        labels: allMappingLabels,
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      onMappingComplete?.();
      mixpanel.track(MIXPANEL_EVENT.TRANSACTION.MAPPING);
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: 'map or split transaction',
      });
      Toast.success('증빙/분할 저장이 완료되었습니다.');
    } else {
      Toast.error('증빙/분할 저장에 실패했습니다.');
    }
  };

  const getSplittedCashFlowType = () => {
    if (transactionLabelMappings?.transactionAmount > 0) {
      return unMappingAmount < 0 ? CashFlowType.OUT : CashFlowType.IN;
    }
    return unMappingAmount < 0 ? CashFlowType.IN : CashFlowType.OUT;
  };

  const cashFlowInPrefill = unMappingAmount > 0 && transactionLabelMappings?.transactionAmount > 0;
  const cashFlowOutPrefill = unMappingAmount > 0 && transactionLabelMappings?.transactionAmount < 0;
  const firstLabel = transactionDetailData?.labels[0];

  const newSplitTransaction = {
    groupLabelId: firstLabel?.groupLabelId ?? null,
    amount: Math.abs(unMappingAmount),
    categoryLabelKey:
      (cashFlowInPrefill || cashFlowOutPrefill) && firstLabel
        ? `${firstLabel.categoryId}_${firstLabel.companyDetailedCategoryId}`
        : null,
    businessEntityId: firstLabel?.businessEntityId ?? null,
    cashFlowType: getSplittedCashFlowType(),
  };

  return (
    <div>
      <div
        style={{
          background: '#F0FFFC',
          padding: '15px 16px 13px 16px',
          borderRadius: 8,
        }}
      >
        <TransactionMappingItem
          cashFlowType={
            transactionLabelMappings.transactionAmount > 0 ? CashFlowType.IN : CashFlowType.OUT
          }
          mode="detail"
          transactionName={transactionLabelMappings.transactionName}
          transactionDescription={transactionLabelMappings.transactionDescription}
          transactionAt={transactionLabelMappings.transactionAt}
          mappingAmount={Math.abs(transactionLabelMappings.transactionAmount)}
          amount={Math.abs(transactionLabelMappings.transactionAmount)}
          currencyCode={transactionDetail?.Data?.data?.currencyCode}
          active
          additionalInfo={
            <Button
              type="link"
              style={{
                padding: 0,
              }}
              onClick={() =>
                overlays.open(({ isOpen, onClose }) => (
                  <TransactionDetailDrawer
                    isOpen={isOpen}
                    onClose={onClose}
                    accountId={transactionLabelMappings.accountId}
                    transactionId={transactionId}
                    accountType={transactionLabelMappings.accountType}
                  />
                ))
              }
            >
              거래내역 상세
            </Button>
          }
        />
      </div>
      <OverlayScrollbarsComponent
        style={{
          overflowY: 'auto',
          maxHeight: '56vh',
        }}
      >
        <Flex direction="column">
          {mappedInvoices.map((m, idx) => {
            const taxInvoice = m;
            const targetCategory = flattenedTree.find(
              (c) => c?.value?.categoryLabelKey === m.categoryLabelKey && !!m.categoryLabelKey,
            );

            const treeList = flattenedTree.filter(
              (treeItem) =>
                treeItem.value?.cashFlowType ===
                ((m?.transactionLabelAmount ?? 0) > 0 ? CashFlowType.IN : CashFlowType.OUT),
            );
            return (
              <>
                <Flex
                  key={idx}
                  align="flex-start"
                  gap={16}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                  }}
                >
                  <CloseOutlined
                    onClick={() => {
                      deleteMappedInvoice(idx);
                    }}
                  />
                  <Flex
                    direction="column"
                    style={{
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: 4,
                      }}
                    >
                      <TaxInvoiceMappingItem
                        businessEntityId={taxInvoice.businessEntityId}
                        type={taxInvoice.taxInvoiceType}
                        date={taxInvoice.reportingDate}
                        contractorName={taxInvoice.counterPartyCompanyName}
                        businessRegistrationNumber={taxInvoice.counterPartyBusinessRegNumber}
                        repItem={taxInvoice.repItems}
                        totalAmount={taxInvoice.totalAmount}
                        mappingAmount={taxInvoice.mappedAmount}
                        counterPartyName={taxInvoice.counterPartyName}
                        icon={<Splitted />}
                        onMappingRefresh={() => {
                          refetch();
                          afterLabelingActions(companyId);
                        }}
                        additionalInfo={
                          <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                              overlays.open(({ isOpen, onClose }) => (
                                <SettlementTaxInvoiceResultDrawer
                                  onClose={onClose}
                                  isOpen={isOpen}
                                  type={taxInvoice.taxInvoiceType}
                                  id={taxInvoice.taxInvoiceId}
                                />
                              ));
                            }}
                          >
                            세금계산서 상세
                          </Button>
                        }
                      />
                    </div>
                    <Flex
                      gap={8}
                      align="flex-start"
                      style={{
                        marginTop: 4,
                      }}
                    >
                      <Form.Item
                        validateStatus={!m.categoryLabelKey ? 'error' : undefined}
                        help={!m.categoryLabelKey ? '계정 분류는 필수값입니다.' : undefined}
                        style={{ flex: 1 }}
                      >
                        <CategorySimpleLabelSelctor
                          treeList={treeList}
                          onSelect={(categoryLabelKey) => {
                            setMappedInvoice(
                              {
                                categoryLabelKey,
                              },
                              idx,
                            );
                          }}
                          targetCategory={targetCategory}
                        />
                      </Form.Item>
                      <GroupLabelSelect
                        style={{ flex: 1 }}
                        value={m.groupLabelId}
                        onChange={(value) => {
                          setMappedInvoice(
                            {
                              groupLabelId: value,
                            },
                            idx,
                          );
                        }}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                <Divider />
              </>
            );
          })}
          {splittedTransactions.map((item, idx) => {
            return (
              <>
                <Flex
                  align="flex-start"
                  gap={16}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                  }}
                >
                  <CloseOutlined
                    onClick={() => {
                      deleteSplittedTransaction(idx);
                    }}
                  />
                  <SplittedItem
                    idx={idx}
                    item={item}
                    currencyCode={transactionDetail?.Data?.data?.currencyCode}
                  />
                </Flex>
                <Divider />
              </>
            );
          })}
          {mode === 'FOLD' ? (
            <Flex align="center" gap={8} direction="column">
              <Dropdown
                menu={{
                  onClick: ({ key }) => {
                    if (key === 'taxInvoice') {
                      onChangeMode?.();
                    } else {
                      addSplittedTransaction(newSplitTransaction);
                    }
                  },
                  items: [
                    {
                      key: 'taxInvoice',
                      label: (
                        <PermissionGuard
                          required={CompanyUserPermission.CAN_MANAGE_SALES_PURCHASES_SETTLEMENT}
                        >
                          <Flex
                            align="center"
                            gap={8}
                            style={{
                              width: '100%',
                            }}
                          >
                            <SearchOutlined />
                            <Typography.Text
                              style={{
                                fontSize: 14,
                              }}
                            >
                              세금계산서로 분할
                            </Typography.Text>
                            <Typography.Text
                              style={{
                                fontSize: 14,
                                color: colorTokens.textDescription,
                              }}
                            >
                              세금계산서 증빙이 있는 거래내역이라면, 검색으로 간편하게 분할하세요.
                            </Typography.Text>
                          </Flex>
                        </PermissionGuard>
                      ),
                    },
                    {
                      key: 'splitTransaction',
                      label: (
                        <Flex
                          align="center"
                          gap={8}
                          style={{
                            width: '100%',
                          }}
                        >
                          <EditOutlined />
                          <Typography.Text
                            style={{
                              fontSize: 14,
                            }}
                          >
                            직접 분할
                          </Typography.Text>
                          <Typography.Text
                            style={{
                              fontSize: 14,
                              color: colorTokens.textDescription,
                            }}
                          >
                            증빙이 없는 거래내역이라면, 직접 금액을 입력하세요.
                          </Typography.Text>
                        </Flex>
                      ),
                    },
                  ],
                }}
              >
                <Button
                  style={{
                    marginTop: 16,
                    width: '100%',
                  }}
                  icon={<PlusOutlined />}
                  type="dashed"
                >
                  거래내역 증빙/분할 추가
                </Button>
              </Dropdown>
              <IssueButton
                style={{
                  width: '100%',
                }}
                onIssueInvoice={() => {
                  closeAll();
                  setSessionStorageItem('tax-invoice-prefill', {
                    price: transactionLabelMappings.transactionAmount,
                    name: transactionLabelMappings.transactionDescription,
                    businessEntityId:
                      transactionLabelMappings?.labelMappings[0]?.businessEntityLabelId,
                  });
                }}
              >
                이 거래내역에 대해 세금계산서 발급
              </IssueButton>
            </Flex>
          ) : (
            <Flex align="center" gap={4} direction="column">
              <Button
                style={{
                  marginTop: 16,
                  width: '100%',
                }}
                icon={<PlusOutlined />}
                type="dashed"
                onClick={() => {
                  addSplittedTransaction(newSplitTransaction);
                }}
              >
                거래내역 분할 추가
              </Button>
              <IssueButton
                style={{
                  width: '100%',
                }}
                onIssueInvoice={() => {
                  closeAll();
                  setSessionStorageItem('tax-invoice-prefill', {
                    price: transactionLabelMappings.transactionAmount,
                    name: transactionLabelMappings.transactionDescription,
                    businessEntityId:
                      transactionLabelMappings?.labelMappings[0]?.businessEntityLabelId,
                  });
                }}
              >
                이 거래내역에 대해 세금계산서 발급
              </IssueButton>
            </Flex>
          )}
          <div
            style={{
              margin: '24px 0px',
            }}
          >
            <MappingTotal
              mappingAmountLabel="금액 합계"
              unmappingAmountLabel="잔액"
              mappingAmount={mappingAmount}
              unmappingAmountError={unMappingAmount > 0}
              unMappingAmount={unMappingAmount}
              currencyCode={transactionDetail?.Data?.data?.currencyCode}
            />
          </div>
          <Flex gap={8} align="center">
            <Button
              style={{
                width: 100,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={onClose}
            >
              취소
            </Button>
            <div
              style={{
                width: '100%',
              }}
              className={css`
                & > span {
                  width: 100%;
                }
              `}
            >
              <Tooltip
                title={
                  <>잔액이 남지 않도록 분할 내역을 추가하고, 계정 분류는 필수로 입력해주세요</>
                }
                placement="top"
                style={{
                  width: '100%',
                }}
              >
                <Button
                  type="primary"
                  disabled={unMappingAmount !== 0 || allMappingLabels.some((m) => !m.categoryId)}
                  style={{
                    width: '100%',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={onMapping}
                  loading={isLoading}
                >
                  증빙/분할 저장
                </Button>
              </Tooltip>
            </div>
          </Flex>
        </Flex>
      </OverlayScrollbarsComponent>
    </div>
  );
};

export default TransactionMappingTargetList;
